import {useIntlTelInput} from '../hooks/useIntlTelInput'
import {cn} from '../lib/utils'
import {InputField} from './InputField'
import {forwardRef, useEffect, useMemo, useRef, useState} from 'react'

export interface InputPhoneNumberProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  'data-testid': string
  /** Uppercase ISO 3166-1 alpha-2 country code (e.g. 'US', 'NL') for the initial phone country flag */
  countryCode?: string
  /** Called whenever phone validity changes — true if intl-tel-input considers the number valid */
  onValidityChange?: (isValid: boolean) => void
  /** Called with the full international number (e.g. +31612345678) whenever the number or country changes */
  onFullNumberChange?: (fullNumber: string) => void
  error?: string
}

export const InputPhoneNumber = forwardRef<HTMLInputElement, InputPhoneNumberProps>(
  (
    {
      className,
      'data-testid': testId,
      onChange,
      onValidityChange,
      onFullNumberChange,
      countryCode,
      value,
      error,
      ...props
    },
    forwardedRef,
  ) => {
    const inputElRef = useRef<HTMLInputElement | null>(null)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const onValidityChangeRef = useRef(onValidityChange)
    useEffect(() => {
      onValidityChangeRef.current = onValidityChange
    }, [onValidityChange])

    const onFullNumberChangeRef = useRef(onFullNumberChange)
    useEffect(() => {
      onFullNumberChangeRef.current = onFullNumberChange
    }, [onFullNumberChange])

    const setRefs = (node: HTMLInputElement | null) => {
      inputElRef.current = node

      if (typeof forwardedRef === 'function') forwardedRef(node)
      else if (forwardedRef && 'current' in forwardedRef) forwardedRef.current = node
    }

    // Derive initial country from the countryCode prop, lowercased for intl-tel-input.
    // Falls back to 'us' when no valid code is provided.
    const initialCountry = useMemo(() => {
      if (typeof countryCode === 'string' && countryCode.length === 2) {
        return countryCode.toLowerCase()
      }
      return 'us'
    }, [countryCode])

    const phoneInputOptions = useMemo(
      () => ({
        initialCountry,
        preferredCountries: ['us', 'gb', 'nl', 'be', 'de', 'fr', 'it', 'ca'],
        separateDialCode: true,
        autoPlaceholder: 'polite' as const,
        nationalMode: true,
      }),
      [initialCountry],
    )

    const phoneInput = useIntlTelInput(inputElRef, phoneInputOptions)

    // Sync the external value into intl-tel-input once loaded
    useEffect(() => {
      if (!phoneInput.isLoaded || !inputElRef.current) return

      const input = inputElRef.current

      const phoneValue = typeof value === 'string' ? value : ''
      if (!phoneValue) return

      // Only set if the input is empty or differs from the provided value
      const currentValue = input.value
      if (!currentValue || currentValue !== phoneValue) {
        phoneInput.setNumber(phoneValue)
      }

      // Report validity — utils.js is guaranteed to be loaded at this point
      onValidityChangeRef.current?.(phoneInput.validateNumber())

      // Re-validate and report full number when the user changes the country flag
      const handleCountryChange = () => {
        onValidityChangeRef.current?.(phoneInput.validateNumber())
        onFullNumberChangeRef.current?.(phoneInput.getNumber())
      }
      input.addEventListener('countrychange', handleCountryChange)

      document.addEventListener('open:countrydropdown', () => {
        setIsDropdownOpen(true)
      })

      document.addEventListener('close:countrydropdown', () => {
        setIsDropdownOpen(false)
      })

      return () => {
        input.removeEventListener('countrychange', handleCountryChange)
      }
    }, [phoneInput.isLoaded, value, phoneInput])

    return (
      <div className={cn('flex w-full flex-col gap-8', className)} data-testid={testId}>
        <div className="phone-input relative">
          <InputField
            inputClassName={cn(isDropdownOpen && 'rounded-t-md rounded-b-none')}
            ref={setRefs}
            id="phone-number"
            label="Phone"
            labelHidden={true}
            type="tel"
            name="phone"
            defaultValue=""
            onChange={(e) => {
              onChange?.(e)
              // Defer so intl-tel-input can process the new value before we read it
              setTimeout(() => {
                onValidityChangeRef.current?.(phoneInput.validateNumber())
                onFullNumberChangeRef.current?.(phoneInput.getNumber())
              }, 0)
            }}
            error={error}
            data-testid="phone-input"
            {...props}
          />
        </div>
      </div>
    )
  },
)

InputPhoneNumber.displayName = 'InputPhoneNumber'
